import type { UserAccessLevelType } from "~/common/services/user/user.types";
import { STATUS_LABELS } from "~/common/workflows/statusLabels";

export type WorkflowStatus =
  | "running"
  | "paused_hitl"
  | "warm_hitl"
  // Review-checkpoint HITL state. Surfaces the same UI as warm_hitl
  // (input-needed panel, HitlDrawerBody) — keep in sync with
  // statusMerge.isHitlStatus / pickDrawerStatusFamily / getSectionOrder.
  | "rpa_review"
  // Non-terminal: instance is parked while the AM sends the reply email.
  // Routes to ActiveDrawerBody (no result, no respond form). Cancel IS shown.
  | "pending_email_send"
  // Non-terminal cold-HITL: parked up to 14 days waiting for the carrier's
  // EPCH eDoc (dec page) to land in L2. No AM action required (reject-only);
  // the daily edoc-poll cron / "Check Now" releases it. Label "Pending eDocs".
  | "pending_edocs"
  | "completed"
  | "failed"
  | "cancelled"
  | "timeout_cancelled"
  | "rejected";

export type WorkflowWindow = "1d" | "7d" | "21d" | "all";

/**
 * How wide a view of the Monitor is being shown. Mirrors the substrate's
 * ScopeMode vocabulary (`workflows/auth/scope_modes.py`) — the string values are
 * the wire contract, so do not rename them without changing both sides.
 *
 * Two distinct ideas, deliberately not conflated (US 25879):
 * - the **ceiling** is the widest scope the user is *permitted* — asserted
 *   server-side from `app.BranchLevelPermission`, never chosen by the client;
 * - the **view** is what they are *currently looking at*, picked with the
 *   "View:" selector and always clamped to the ceiling server-side.
 *
 * So sending a wider scope than you are entitled to narrows silently rather
 * than erroring — the UI cannot grant itself access by asking.
 */
export type MonitorScope = "self" | "branch" | "region" | "all";

/** Human labels for the "View:" selector, keyed by scope. */
export const MONITOR_SCOPE_LABELS: Record<MonitorScope, string> = {
  self: "Mine",
  branch: "Branch",
  region: "Region",
  all: "All",
};

/** Narrow an untrusted query-string value to a MonitorScope. */
export function isMonitorScope(value: unknown): value is MonitorScope {
  return (
    value === "self" ||
    value === "branch" ||
    value === "region" ||
    value === "all"
  );
}

/**
 * Options offered for a given ceiling, narrowest first — the July rule
 * (re-agreed with Daniel, 2026-09-28). Branch-level ceilings (Branch leaders,
 * Producers, Servicers) choose Mine or Branch. A Regional leader's one view is
 * their region and Central's is everything: no control.
 *
 * A single-option result means "render no control at all".
 */
export function monitorScopeOptions(ceiling: MonitorScope): MonitorScope[] {
  switch (ceiling) {
    case "branch":
      return ["self", "branch"];
    case "region":
      return ["region"];
    case "all":
      return ["all"];
    default:
      return ["self"];
  }
}

/**
 * Read-model row shape for the Monitor grid. Sourced from the Product API
 * tracker in production; currently backed by demo data until the substrate
 * contract + tracker are wired up.
 *
 * `triggeredByRegion` / `triggeredByBranch` / `triggeredByLocation` are the
 * **purview snapshot** captured at admit time (per substrate schema §8.1c).
 * They drive hierarchy visibility: BranchLeader sees rows where
 * `triggeredByBranch === user.branchScope`, RegionalLeader matches on
 * `triggeredByRegion`, CentralTeamMember sees everything.
 */
export interface WorkflowInstanceRow {
  instanceId: string;
  workflowType: string;
  workflowTitle: string;
  status: WorkflowStatus;
  currentStepName: string | null;
  triggeredByName: string;
  triggeredByEmail: string;
  triggeredByRegion: string;
  triggeredByBranch: string;
  triggeredByLocation: string;
  clientName: string | null;
  policyNumber: string | null;
  startedAt: string; // ISO-8601
  updatedAt: string;
  endedAt: string | null;
  /** Story 23467, AC-17: MLflow trace ID — null if not yet persisted. */
  mlflowTraceId: string | null;
  /**
   * Story 23467, AC-17: pre-assembled MLflow trace deep-link URL. Non-null
   * only when both mlflow_trace_id and mlflow_ui_url are set on the substrate.
   */
  mlflowTraceUrl: string | null;
  stepIndex?: number | null;
  totalSteps?: number | null;
  stepDisplayName?: string | null;
  /**
   * True when the run's persisted result carries any non-empty ``*_warning``
   * key. Renders as a "*" beside the status badge in the Monitor grid and the
   * detail drawer — the run finished (or parked) but something needs the AM's
   * eye (e.g. "attach the dec page manually", "issue the endorsement in Epic").
   */
  hasWarnings?: boolean;
  /**
   * Warning acknowledgement (2026-10-06): server-computed — a warning line on
   * this run is still open ("Completed*"). Null when the API predates it; the
   * badge then falls back to `hasWarnings`.
   */
  needsAcknowledgement?: boolean | null;
  /**
   * ADO #26763 (A6). Passed straight through from the substrate's
   * `needs_you` projection — the SAME `is_notifiable` rule the nav badge and
   * the Needs-You KPI tile count on. NEVER RE-DERIVED from `status` here:
   * doing so would let this row's action cell (`RunActionsCell`) disagree
   * with the badge's own definition of "needs you". `undefined`/`false` both
   * mean the row offers neither "finish in Outlook ↗" nor Nudge.
   */
  needsYou?: boolean;
  /**
   * Leader governance phase 3: when the run started waiting (ISO), passed
   * straight through from the substrate's `waiting_since`. Drives the Runs
   * table's "Waiting" column on Needs You rows; null = not waiting, or a wait
   * older than the stamp (shown as "unknown", never as zero).
   */
  decisionKey?: string | null;
  /** A workflow-skill analysis's level of detail (document-analysis S3), else absent. */
  detailLevel?: string | null;
  waitingSince?: string | null;
  /**
   * Leader governance phase 3: the viewer owns this run — the server's
   * `is_mine`, passed straight through. Decides "finish in Outlook ↗" (your
   * own waiting run) versus Nudge (a teammate's). Optional like `needsYou`;
   * `mapInstanceToRow` always sets it.
   */
  isMine?: boolean;
  /**
   * US 28636: an external run's link (`external_url`); null for a substrate run.
   * The Monitor sends the run's owner here instead of opening the drawer.
   */
  externalUrl?: string | null;
}

/**
 * Subset of UserSessionDetails the Monitor page needs — drives hierarchy
 * filtering and personalizes demo data so every logged-in user sees
 * something sensible regardless of their real org.
 */
export interface MonitorViewerContext {
  email: string;
  name: string;
  accessLevel: UserAccessLevelType;
  region: string | null;
  branch: string | null;
  location: string | null;
  // NOTE: the scope ceiling and landing view are deliberately NOT here.
  //
  // They are resolved per request and arrive on the monitor API response
  // (`scopeCeiling` / `defaultScope`) rather than in the loader's viewer
  // context, which is built from the user session. A ceiling is a per-request
  // fact: session-caching it meant a stale tab kept requesting a scope the
  // caller no longer had — every poll clamped — and impersonation needed its
  // own session refresh to compensate. Read them from the fetcher data.
}

/**
 * Leader governance phase 3: where a watched mailbox sits, projected by the
 * monitor API from the registry list so the Mailboxes tile can follow the
 * drill (ruling 6). Placement and state only — no triage numbers.
 */
export interface MonitorMailboxRow {
  mailbox_key: string;
  owning_branch: string | null;
  owning_region: string | null;
  is_watched: boolean;
  /**
   * `off` = Paused. With `is_watched` it decides "live" exactly as the
   * Mailboxes page's "Live · N of M" pill does (`isLiveMailbox`), for the
   * Mailboxes tile's "{live} of {watched}". Null when an older API omits it.
   */
  intake_mode: string | null;
  demoted_at: string | null;
  /** The viewer's own or a linked mailbox: Mine's Mailboxes tile counts only these. */
  involves_me: boolean;
}

/**
 * AC-8: final result envelope for a completed workflow instance.
 * Matches the wire shape of GET /api/workflows/instances/{id}/result
 * (PluginWorkflowController.WorkflowResultResponse).
 */
export interface WorkflowResultEnvelope {
  instance_id: string;
  status: string;
  completed_at: string | null;
  result: Record<string, unknown> | null;
}

/**
 * Display priority for the Status column. Lower values sort first. Drives the
 * initial grid sort: Warm Hold → Input Needed → Running → Completed → Error → Cancelled → Rejected.
 */
export const STATUS_ORDER: Record<WorkflowStatus, number> = {
  warm_hitl: 0,          // "Warm Hold" — top priority
  paused_hitl: 1,        // "Input Needed"
  rpa_review: 1,         // same UX bucket as paused_hitl (review-checkpoint HITL)
  running: 2,
  pending_email_send: 2, // non-terminal: AM needs to send email; same active bucket as running
  pending_edocs: 2,      // non-terminal cold wait for carrier eDocs; no AM action
  completed: 3,          // "Completed"
  failed: 4,             // "Error"
  cancelled: 5,
  timeout_cancelled: 5,
  rejected: 6,           // deliberate terminal outcome
};

export const STATUS_LABEL: Record<WorkflowStatus, string> = {
  failed: STATUS_LABELS.failed,
  paused_hitl: STATUS_LABELS.paused_hitl,
  warm_hitl: STATUS_LABELS.warm_hitl,
  rpa_review: STATUS_LABELS.warm_hitl,
  running: STATUS_LABELS.running,
  pending_email_send: STATUS_LABELS.pending_email_send,
  pending_edocs: STATUS_LABELS.pending_edocs,
  cancelled: STATUS_LABELS.cancelled,
  timeout_cancelled: STATUS_LABELS.timeout_cancelled,
  rejected: STATUS_LABELS.rejected,
  completed: STATUS_LABELS.completed,
};

// `null` means "no cutoff" (the "all" window) rather than an arbitrarily large
// minute count, so filterForMonitor's intent reads directly from the table.
const WINDOW_CUTOFF_MINS: Record<WorkflowWindow, number | null> = {
  "1d": 24 * 60,
  "7d": 7 * 24 * 60,
  "21d": 21 * 24 * 60,
  all: null,
};

/**
 * Statuses that mean the workflow has finished — no further user action is
 * possible. Ordered newest-first within the monitor grid so the most recent
 * outcome surfaces at the top of the terminal block.
 */
const TERMINAL_STATUSES = new Set<WorkflowStatus>([
  "failed",
  "cancelled",
  "timeout_cancelled",
  "rejected",
  "completed",
]);

/**
 * Window-only filter — drops rows older than the active window cutoff but
 * preserves arrival order. Use this on the live grid path so AG Grid is the
 * single source of truth for sort: applying STATUS_ORDER both in the route
 * and in the colDef caused the user's chosen header click to get
 * overwritten on every poll/SignalR refresh.
 *
 * The "all" window has no cutoff — every fetched row passes through unfiltered.
 */
export function filterForMonitor(
  rows: WorkflowInstanceRow[],
  window: WorkflowWindow,
  nowOverride?: Date,
): WorkflowInstanceRow[] {
  const cutoffMins = WINDOW_CUTOFF_MINS[window];
  if (cutoffMins == null) return rows;
  const now = nowOverride ?? new Date();
  const cutoff = now.getTime() - cutoffMins * 60_000;
  return rows.filter((r) => new Date(r.startedAt).getTime() >= cutoff);
}

export function filterAndSortForMonitor(
  rows: WorkflowInstanceRow[],
  window: WorkflowWindow,
  nowOverride?: Date,
): WorkflowInstanceRow[] {
  const visible = filterForMonitor(rows, window, nowOverride);
  return visible.sort((a, b) => {
    const pa = STATUS_ORDER[a.status];
    const pb = STATUS_ORDER[b.status];
    if (pa !== pb) return pa - pb;
    // Within a status group, direction depends on whether the AM still has
    // anything to do with these rows:
    //   - non-terminal (failed-but-action-required is not in this set; only
    //     paused_hitl + running): oldest first → reviewer drains the backlog
    //     in arrival order so SLA-sensitive items don't get buried.
    //   - terminal (failed/cancelled/timeout_cancelled/completed): newest
    //     first → the AM's most recent run is at the top, where they expect
    //     to find it after coming back to the page.
    if (TERMINAL_STATUSES.has(a.status)) {
      return b.startedAt.localeCompare(a.startedAt);
    }
    return a.startedAt.localeCompare(b.startedAt);
  });
}
